<template>
  <div class="net-zoom-controls" ref="zoomarea" @contextmenu.prevent="showScaleContextmenu">
    <button
      ref="zoomButton"
      type="button"
      class="button-icon ph-1"
      @click="toggleZoomPanel"
    >
      <lucide-icon name="zoom-in" />
    </button>

    <floating-element
      class="net-zoom-controls__popup"
      :visible="zoomPanel.visible"
      :x="zoomPanel.x"
      :y="zoomPanel.y"
      :width="zoomPanel.width"
      :height="zoomPanel.height"
      :min-width="60"
      :min-height="40"
      transition-name="transition-pop-in-up"
      x-alignment="center"
      y-alignment="bottom"
      :non-closing-elements="zoomButton ? [zoomButton] : []"
      @click:outside="zoomPanel.visible = false"
    >
      <template #default>
        <div class="net-zoom-controls__content" @contextmenu.prevent="showScaleContextmenu">
          <button
            v-if="settingsStore.netScaleIconsVisible"
            type="button"
            class="button-icon ph-1"
            @click="roundedScale *= 0.9"
          >
            <lucide-icon name="zoom-out" />
          </button>
          <input-range
            v-if="settingsStore.netScaleBarVisible"
            :width="200"
            :min="roundedMinMaxScale.min"
            :max="roundedMinMaxScale.max"
            :value="roundedScale"
            :limit="roundedMinMaxScale.max"
            class="net-zoom-controls__zoombar"
            :class="{ '-with-icons': settingsStore.netScaleIconsVisible }"
            :tooltip-enabled="true"
            @update:value="roundedScale = $event"
          >
            <template #tooltip="{ value }">
              {{ value }}%
            </template>
            <template #after>
              <div
                class="net-zoom-controls__zoombar-after"
                :class="{ '-with-icons': settingsStore.netScaleIconsVisible }"
              >
                <input
                  type="number"
                  class="net-zoom-controls__zoomvalue"
                  :value="roundedScale"
                  :min="roundedMinMaxScale.min"
                  :max="roundedMinMaxScale.max"
                  @input="onRoundedScaleInput"
                  @change="onRoundedScaleChange"
                  @keydown.enter="($event.target as HTMLInputElement).blur()"
                />%
              </div>
            </template>
          </input-range>
      
          <button
            v-if="settingsStore.netScaleIconsVisible"
            type="button"
            class="button-icon ph-1"
            @click="roundedScale *= 1.1111"
          >
            <lucide-icon name="zoom-in" />
          </button>
      
          <button
            v-if="
              !settingsStore.netScaleBarVisible &&
              !settingsStore.netScaleIconsVisible
            "
            type="button"
            class="button-icon"
            @click="showScaleContextmenu"
          >
            <lucide-icon name="more-horizontal" />
          </button>
        </div>
      </template>
    </floating-element>


    <context-menu
      v-if="scaleContextmenu.visible"
      :x="scaleContextmenu.x"
      :y="scaleContextmenu.y"
      x-alignment="right"
      y-alignment="bottom"
      @close="scaleContextmenu.visible = false"
    >
      <button
        v-if="settingsStore.netScaleBarVisible"
        type="button"
        class="context-menu__entry"
        @click="changeSetting('netScaleBarVisible', false)"
      >
        {{ $t("actions.hideZoomBar") }}
      </button>
      <button
        v-else
        type="button"
        class="context-menu__entry"
        @click="changeSetting('netScaleBarVisible', true)"
      >
        {{ $t("actions.showZoomBar") }}
      </button>
      <button
        v-if="settingsStore.netScaleIconsVisible"
        type="button"
        class="context-menu__entry"
        @click="changeSetting('netScaleIconsVisible', false)"
      >
        {{ $t("actions.hideZoomIcons") }}
      </button>
      <button
        v-else
        type="button"
        class="context-menu__entry"
        @click="changeSetting('netScaleIconsVisible', true)"
      >
        {{ $t("actions.showZoomIcons") }}
      </button>
    </context-menu>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import InputRange from '@core/input/InputRange.vue';
import ContextMenu from '@core/ui/ContextMenu.vue';
import LucideIcon from '@core/ui/LucideIcon.vue';
import FloatingElement from '@core/ui/FloatingElement.vue';
import { useSettingsStore } from '@editor/store/settings.store';

const props = defineProps<{
  scale: number;
}>();
const emit = defineEmits<{
  (e: 'update:scale', value: number): void;
}>();

const zoomButton = ref<HTMLElement>();
const zoomarea = ref<HTMLElement>();
const settingsStore = useSettingsStore();

const MIN_SCALE = 0.1;
const MAX_SCALE = 2;

const roundedMinMaxScale = computed(() => ({
  min: MIN_SCALE * 100,
  max: MAX_SCALE * 100
}));

const roundedScale = computed({
  get() {
    return Math.round(props.scale * 100);
  },
  set(value: number) {
    if (isNaN(value)) return;
    const { min, max } = roundedMinMaxScale.value;
    if (value > max) value = max;
    if (value < min) value = min;
    emit("update:scale", value / 100);
  }
});

function onRoundedScaleInput(event: Event) {
  const target = event.target as HTMLInputElement;
  const value = target.valueAsNumber;
  const { min, max } = roundedMinMaxScale.value;
  if (value < min) {
    return;
  } else if (value > max) {
    return;
  }
  roundedScale.value = value;
}
function onRoundedScaleChange(event: Event) {
  const target = event.target as HTMLInputElement;
  roundedScale.value = target.valueAsNumber;
}

// ==============================
// ZOOM PANEL

const zoomPanel = reactive({
  visible: false,
  x: 100,
  y: 100,
  width: 300,
  height: 40
});

function toggleZoomPanel() {
  zoomPanel.visible = !zoomPanel.visible;
  if (zoomPanel.visible && zoomarea.value) {
    const rect = zoomarea.value.getBoundingClientRect();
    zoomPanel.x = rect.right - rect.width / 2;
    zoomPanel.y = rect.top;
    updateZoomPanelWidth();
  }
}

function updateZoomPanelWidth() {
  if (settingsStore.netScaleBarVisible) {
    zoomPanel.width = 300;
  } else {
    zoomPanel.width = 74;
  }
}

// ==============================
// SCALE CONTEXT MENU

const scaleContextmenu = reactive({
  visible: false,
  x: 0,
  y: 0
});

function showScaleContextmenu() {
  scaleContextmenu.visible = true;

  if (!zoomarea.value) return;
  const rect = zoomarea.value.getBoundingClientRect();
  scaleContextmenu.x = rect.right;
  scaleContextmenu.y = rect.top - (zoomPanel.visible ? zoomPanel.height : 0);
}

function changeSetting<K extends "netScaleBarVisible" | "netScaleIconsVisible">(key: K, value: boolean) {
  settingsStore.set(key, value);
  updateZoomPanelWidth();
}
</script>

<style lang="scss">
.net-zoom-controls {
  display: flex;
  align-items: center;
  pointer-events: auto;
  $after-width: 4.5rem;

  &__popup {
    display: flex;
    align-items: center;
    overflow: hidden;
    background-color: var(--color-background-deep);
    border-radius: 0.75rem;
    box-shadow: 0px 1px 7px var(--color-shadow-3);
  }
  &__content {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.5rem;
    gap: 0.5rem;
    overflow: hidden;
  }

  &__zoombar {
    width: 100%;
    min-width: 11.5rem + $after-width;
    min-width: 0;
    padding-block: 0.5rem;
    &:first-child {
      padding-left: 0.5rem;
    }

    &.-with-icons {
      min-width: 12rem;
      margin-right: 0;
    }
  }

  &__zoomicons {
    display: flex;
    justify-content: center;
    width: $after-width;
    gap: 2px;
  }

  &__zoomicons,
  &__zoombar-after {
    flex-shrink: 0;
  }

  &__zoombar-after {
    width: 3rem;
    position: relative;
    background-color: var(--color-background-deep);
    transition: opacity 150ms;
    transition-delay: 300ms;
    padding: 0.25rem;
    border-radius: 1rem;

    &.-with-icons {
      display: none;
      pointer-events: none;
      width: $after-width;
      opacity: 0;
      margin-right: -$after-width;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
      text-align: center;
    }
  }
  &__zoombar:active &__zoombar-after {
    opacity: 1;
    transition-delay: 0ms;
  }
  &__zoomvalue {
    display: inline-block;
    width: 1.65rem;
    padding: 0;
    border: none;
    outline: 1px solid transparent;
    margin: 0;
    outline-offset: 2px;
    text-align: right;
    white-space: nowrap;

    &:focus-visible {
      outline: 1px solid var(--color-action);
    }

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      appearance: none;
    }
  }
}
</style>